import React from 'react'
import { Box, SelectChangeEvent, StandardTextFieldProps, formControlClasses, outlinedInputClasses } from '@mui/material'
import { FieldHookConfig, useField } from 'formik'
import { isInvalid } from '@carsys/form/src/utils'
import { useTranslation } from 'react-i18next'
import { TextField } from '@carsys/ui'
import { CountryAreaCodeSelect } from '@cbgms/components/form/PhoneNumber/CountryAreaCodeSelect'
import { cleanPhoneNumber, findCountryAreaCodeByDialCode, findPhoneNumberAreaCode } from '@cbgms/components/form/PhoneNumber/utils'
import { formatPhonenumberByCountryConvention } from '@cbgms/components/form/PhoneNumber/formatPhonenumberByCountryConvention'
import { CountryCode } from '@carsys/enums/country-code'

export interface PhoneNumberInputFormikProps
  extends Pick<FieldHookConfig<string>, 'name' | 'validate'>,
    Omit<StandardTextFieldProps, 'name'> {
  labelTranslationKey?: string
  defaultCountry: CountryCode
}

export const PhoneNumberInputFormik: React.FC<PhoneNumberInputFormikProps> = ({
  labelTranslationKey = 'setting_my_shop.telephonenumber',
  defaultCountry,
  sx,
  required = true,
  onChange,
  validate,
  name,
  disabled,
  ...rest
}) => {
  const { t } = useTranslation()

  const [input, meta, helpers] = useField<string>({ name, validate })
  const [areaCode, setAreaCode] = React.useState(findPhoneNumberAreaCode(defaultCountry, input.value || ''))
  const [phoneNumber, setPhoneNumber] = React.useState(cleanPhoneNumber(input.value, areaCode, defaultCountry))

  const handleOnBlurPhoneNumber = () => {
    helpers.setTouched(true)
    if (!cleanPhoneNumber(input.value, areaCode, defaultCountry)) {
      helpers.setValue('')
    }
  }

  const handleOnFocusPhoneNumber = () => {
    if (input.value) {
      const formatOnFocus = formatPhonenumberByCountryConvention(defaultCountry, input.value)
      helpers.setValue(formatOnFocus)
      return
    }
    if (!cleanPhoneNumber(input.value, areaCode, defaultCountry)) {
      helpers.setValue(areaCode.dialCode)
    }
  }

  const handleOnchangePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const areaCodeChangeCheck = findPhoneNumberAreaCode(areaCode.code, event.target.value)
    // areaCode has changed in the phonenumber, so we update the dropdown through setUpdateAreaCode
    if (areaCodeChangeCheck && areaCodeChangeCheck.dialCode !== areaCode.dialCode) {
      setAreaCode(areaCodeChangeCheck)
    }
    setPhoneNumber(event.target.value)
    // we only run the onchange with a different value if we find either a new areaCode through the event
    // or if the formatter results in a new (formatted) string
    const formatPhonenumber = formatPhonenumberByCountryConvention(defaultCountry, event.target.value)
    helpers.setValue(formatPhonenumber)
  }

  const handleOnchangeAreaCode = (event: SelectChangeEvent<string>) => {
    setAreaCode(findCountryAreaCodeByDialCode(areaCode.code, event.target.value))
    helpers.setValue(`${event.target.value}${cleanPhoneNumber(phoneNumber, areaCode, defaultCountry)}`)
  }

  React.useEffect(() => {
    if (input.value !== '' && !disabled) {
      helpers.setValue(input.value)
    }
  }, [])

  return (
    <Box sx={{ ...sx, display: 'flex !important', [`& > .${formControlClasses.root}`]: { flex: '1' } }}>
      <CountryAreaCodeSelect onChange={handleOnchangeAreaCode} areaCode={areaCode} />
      <TextField
        sx={{
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderRadius: Theme => `0 ${Theme.shape.borderRadius}px ${Theme.shape.borderRadius}px 0`
          }
        }}
        data-testid='phone-number-textfield'
        label={t(labelTranslationKey)}
        required={required}
        helperText={isInvalid(meta) ? meta.error : ' '}
        error={isInvalid(meta)}
        value={input.value}
        disabled={disabled || false}
        onChange={handleOnchangePhoneNumber}
        onFocus={handleOnFocusPhoneNumber}
        onBlur={handleOnBlurPhoneNumber}
        InputProps={{
          inputProps: { 'data-testid': 'phone-number-textfield-input' }
        }}
        inputProps={{
          maxLength: 18
        }}
        {...rest}
      />
    </Box>
  )
}
